import { Leaf, Users, Shield, Heart } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import sustainabilityImage from "@/assets/sustainability.jpg";
import backgroundNoise from "@/assets/background-noise.svg";

const Commitments = () => {
  const heroReveal = useScrollReveal();
  const tabsReveal = useScrollReveal();
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 pt-24 md:pt-32 pb-12 md:pb-20 bg-foreground text-background overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Our <span className="text-accent italic">Commitments</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-background/80 leading-relaxed max-w-3xl mx-auto">
              Dedicated to sustainable growth, social responsibility, inclusivity, and ethical marketing practices
            </p>
          </div>
        </div>
      </section>

      {/* Tabs for Different Commitments */}
      <section className="py-12 md:py-20 px-4">
        <div 
          ref={tabsReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            tabsReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <Tabs defaultValue="sustainability" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-8 md:mb-12 h-auto">
              <TabsTrigger value="sustainability" className="text-sm md:text-base py-3">Sustainability</TabsTrigger>
              <TabsTrigger value="social" className="text-sm md:text-base py-3">Social Impact</TabsTrigger>
              <TabsTrigger value="inclusivity" className="text-sm md:text-base py-3">Inclusivity</TabsTrigger>
              <TabsTrigger value="ethics" className="text-sm md:text-base py-3">Ethics</TabsTrigger>
            </TabsList>

            {/* Environmental Sustainability */}
            <TabsContent value="sustainability">
              <div className="space-y-6 md:space-y-8">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-accent rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Leaf size={28} className="md:w-8 md:h-8 text-accent-foreground" />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Environmental Sustainability</h2>
                    <p className="text-sm md:text-base text-muted-foreground">Empowering brands to grow while protecting the planet</p>
                  </div>
                </div>

                <div className="aspect-video rounded-2xl md:rounded-3xl overflow-hidden mb-6 md:mb-8">
                  <img 
                    src={sustainabilityImage} 
                    alt="Sustainability" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
                  At Northquest Marketing, our mission to streamline digital marketing is deeply connected to our responsibility to protect the world we share. As a growing firm, we are committed to embedding sustainability from the start.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8">
                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Cutting Our Emissions</h3>
                    <ul className="space-y-2 text-sm md:text-base text-muted-foreground">
                      <li>• Hybrid & Remote Work reducing commuting emissions</li>
                      <li>• Digital-First Operations minimizing paper use</li>
                      <li>• Green-certified hosting for all digital infrastructure</li>
                    </ul>
                  </div>

                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Catalyzing Climate Action</h3>
                    <ul className="space-y-2 text-sm md:text-base text-muted-foreground">
                      <li>• Green Brand Positioning</li>
                      <li>• Eco-Efficient Campaigns</li>
                      <li>• Sustainable Storytelling</li>
                      <li>• Low-Carbon Digital Solutions</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-2xl md:rounded-3xl p-6 md:p-8">
                  <h3 className="text-lg md:text-xl font-semibold mb-4">Our Initial Targets</h3>
                  <ul className="space-y-3 text-sm md:text-base text-muted-foreground">
                    <li>• <strong>2026:</strong> Reduce operational energy consumption by 30%</li>
                    <li>• <strong>2030:</strong> 100% of websites hosted on green-certified servers</li>
                    <li>• <strong>2028:</strong> 80% of campaigns include sustainability impact tracking</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Social Responsibility */}
            <TabsContent value="social">
              <div className="space-y-6 md:space-y-8">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-accent rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Heart size={28} className="md:w-8 md:h-8 text-accent-foreground" />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Social Responsibility</h2>
                    <p className="text-sm md:text-base text-muted-foreground">Using digital marketing as a force for good</p>
                  </div>
                </div>

                <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
                  At Northquest Marketing, we extend our mission beyond our clients to the communities and causes we serve. We believe digital marketing can be a force for good, helping organizations thrive, economies prosper, and people progress.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Building Digital Skills for All</h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4">
                      No-cost training programs to empower small business owners, entrepreneurs, and underrepresented groups.
                    </p>
                    <ul className="space-y-2 text-sm md:text-base text-muted-foreground">
                      <li>• <strong>Goal:</strong> Upskill 10,000 individuals by 2030</li>
                      <li>• <strong>Focus:</strong> Social enterprises, women-led businesses, youth entrepreneurs</li>
                    </ul>
                  </div>

                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6 md:p-8">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Pro Bono Projects</h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-4">
                      Partnership with nonprofits to deliver free digital strategy and campaign support.
                    </p>
                    <ul className="space-y-2 text-sm md:text-base text-muted-foreground">
                      <li>• <strong>Goal:</strong> 30 projects globally by 2030</li>
                      <li>• <strong>Areas:</strong> Sustainability, education, economic inclusion</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-muted/30 border border-border rounded-2xl md:rounded-3xl p-6 md:p-8">
                  <h3 className="text-lg md:text-xl font-semibold mb-4">Sustainable Growth Commitment</h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    We measure success not just in revenue growth, but in reduced environmental impact, broadened economic opportunity, and maintained ethical excellence in every campaign and client relationship.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Inclusivity */}
            <TabsContent value="inclusivity">
              <div className="space-y-6 md:space-y-8">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-accent rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Users size={28} className="md:w-8 md:h-8 text-accent-foreground" />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Commitment to Inclusivity</h2>
                    <p className="text-sm md:text-base text-muted-foreground">Celebrating diversity, driving creativity, building growth for all</p>
                  </div>
                </div>

                <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
                  At Northquest Marketing, diversity and inclusion are not just values—they are the foundation of how we work, create, and lead. We believe the most impactful strategies emerge when different perspectives, experiences, and talents meet to solve problems together.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-3">Women in Digital</h3>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Mentoring and skill-building for women in marketing and leadership
                    </p>
                  </div>

                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-3">Creative Voices</h3>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Platform for showcasing diverse storytelling and cultural representation
                    </p>
                  </div>

                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-3">Open Collaboration</h3>
                    <p className="text-sm md:text-base text-muted-foreground">
                      Every project team reflects diversity in thought and background
                    </p>
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-2xl md:rounded-3xl p-6 md:p-8">
                  <h3 className="text-lg md:text-xl font-semibold mb-4">Inclusive Impact Goals by 2030</h3>
                  <ul className="space-y-3 text-sm md:text-base text-muted-foreground">
                    <li>• Upskill 10,000 people from underrepresented communities</li>
                    <li>• Dedicate 20% of pro bono work to equality organizations</li>
                    <li>• Launch 1 annual research report on diversity in digital marketing</li>
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Ethical Marketing */}
            <TabsContent value="ethics">
              <div className="space-y-6 md:space-y-8">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-accent rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Shield size={28} className="md:w-8 md:h-8 text-accent-foreground" />
                  </div>
                  <div>
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2">Ethical Marketing</h2>
                    <p className="text-sm md:text-base text-muted-foreground">Building trust through transparency, responsibility, and respect</p>
                  </div>
                </div>

                <p className="text-base md:text-lg text-muted-foreground mb-6 leading-relaxed">
                  At Northquest Marketing, we believe marketing should not only drive growth but also uphold the highest ethical standards. Our commitment is to create strategies that are transparent, respectful, and responsible.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Transparency in Action</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Clear communication on strategies and costs</li>
                      <li>• Open reporting with true performance metrics</li>
                      <li>• No misleading claims or manipulative tactics</li>
                    </ul>
                  </div>

                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Responsibility to Audiences</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Data privacy with industry-leading safeguards</li>
                      <li>• Content to inform and inspire, never exploit</li>
                      <li>• Inclusive storytelling avoiding stereotypes</li>
                    </ul>
                  </div>

                  <div className="bg-card border border-border rounded-2xl md:rounded-3xl p-6">
                    <h3 className="text-lg md:text-xl font-semibold mb-4">Integrity in Partnerships</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Vetting for ethical alignment</li>
                      <li>• Saying no to harmful projects</li>
                      <li>• Prioritizing long-term trust over short-term wins</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-2xl md:rounded-3xl p-6 md:p-8">
                  <h3 className="text-lg md:text-xl font-semibold mb-4">Our Initial Targets</h3>
                  <ul className="space-y-3 text-sm md:text-base text-muted-foreground">
                    <li>• 100% of campaigns follow our Ethical Marketing Code</li>
                    <li>• Annual Marketing ethics training for all staff and collaborators</li>
                    <li>• Ongoing audits for data privacy and advertising law compliance</li>
                    <li>• Publish an annual Ethical Marketing Impact Report by 2026</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Commitments;
